import React, { useEffect, useRef, useState } from 'react'
import Analytics from 'utils/googleAnalytics'
import { useCookies } from 'react-cookie'

import environment from 'environment'

import { useActionless, useAppSelector } from 'redux/hooks'
import { CardType, ADD_METHODS, ADD_METHOD_NAMES } from 'types/deck'
import { SET_CACHEABLES_STATE } from 'redux/cacheables/actions/types'
import { QuickAddOptions } from 'redux/cacheables/initialState'

import CardAutocomplete from 'components/formElements/CardAutocomplete'
import DetailedAddModal from 'components/deckPage/modals/DetailedAddModal'
import PhatButton from 'components/formElements/PhatButton'
import ArchidektDropdown, { createKeybindLabel } from 'components/elements/ArchidektDropdown'
import Modal from 'components/elements/Modal'
import Icon, { IconTypes } from 'components/elements/Icon'
import { SimpleButton } from 'components/formElements/Button'
import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './quickAddCard.module.scss'

type Props = {
  onCardSelected: (card: CardType, matchCheck: 'name' | 'uid' | 'never', incrementOnExists: boolean) => void
}

const QuickAddCard = ({ onCardSelected }: Props) => {
  // Ref passed as such due to the way the child is implementd as a ref callback
  // TODO - would love to change the child here
  let ref = useRef<any>(null)

  const categories = useAppSelector(state => state.deck.categories)
  const quickAddOptions = useAppSelector(state => state.cacheables.quickAddOptions)

  const [setCachableState] = useActionless(SET_CACHEABLES_STATE)

  const defaultAddToCategory = quickAddOptions.addToCategoryName
    ? categories[quickAddOptions?.addToCategoryName]?.name || null
    : null

  const [query, setQuery] = useState('')
  const [detailedAddModal, setDetailedAddModal] = useState(false)
  const [addToCategory, setAddToCategory] = useState<string | null>(defaultAddToCategory)

  const { dontClearOnSelect, smartQuickAdd, incrementOnDuplicate, addType } = quickAddOptions

  const [viewSmartDetailsInfo, setViewSmartDetailsInfo] = useState(false)

  const handleUpdateOptions = (option: Partial<QuickAddOptions>) =>
    setCachableState({ quickAddOptions: { ...quickAddOptions, ...option } })

  const handleKeyDown = (e: KeyboardEvent) => {
    const controlKey = environment.getOnMac() ? e.metaKey : e.ctrlKey

    if (basicKeybindSkips(e)) return

    if (e.key === "'" && controlKey) {
      handleUpdateOptions({ addType: ADD_METHODS.QUICK_ADD })
      ref?.current.focus()
    }

    if (e.key === '.' && controlKey) {
      handleUpdateOptions({ addType: ADD_METHODS.SYNTAX_QUICK_ADD })
      ref?.current.focus()
    }

    if (e.key === ';' && controlKey) {
      handleUpdateOptions({ addType: ADD_METHODS.DETAILED_ADD })
      setDetailedAddModal(true)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleCardSelected = (card: CardType) => {
    if (!dontClearOnSelect) {
      setQuery('')
      ref.current.focus()
    }

    const cardToAdd = addToCategory ? { ...card, categories: [addToCategory] } : card

    Analytics.signalEvent(`Card added - ${addType === ADD_METHODS.SYNTAX_QUICK_ADD ? 'syntax quick add' : 'quick add'}`)

    onCardSelected(cardToAdd, quickAddOptions.matchExistingBy, quickAddOptions.incrementOnDuplicate)
  }

  const handleSelecteAddToCategory = (categoryName: string | null) => {
    handleUpdateOptions({ addToCategoryName: categoryName || '' })
    setAddToCategory(categoryName)
  }

  return (
    <div className={styles.container}>
      <label className={styles.header}>
        {addType === ADD_METHODS.QUICK_ADD && 'Quick add'}
        {addType === ADD_METHODS.SYNTAX_QUICK_ADD && 'Syntax add'}

        {addToCategory && (addType === ADD_METHODS.QUICK_ADD || addType === ADD_METHODS.SYNTAX_QUICK_ADD) && (
          <span className={styles.maybeboardIndicator}>({addToCategory})</span>
        )}
      </label>
      {(addType === ADD_METHODS.QUICK_ADD || addType === ADD_METHODS.SYNTAX_QUICK_ADD) && (
        <CardAutocomplete
          key={`${addType}-${smartQuickAdd}`}
          fetchCard
          smartFilter={smartQuickAdd}
          scryfallSearch={addType === ADD_METHODS.SYNTAX_QUICK_ADD}
          keepOpenOnSelect={dontClearOnSelect}
          limitedSearchOptions={{ includeArtCards: false }} // Hard disabling art cards in quick add for now. Will revisit later if it becomes a problem
          ref={ref}
          value={query}
          onChange={(_e: any, { value }: any) => setQuery(value)}
          onSelect={handleCardSelected}
          icon="add"
          onFocus={() => ref.current?.select()}
          placeholder={
            addType === ADD_METHODS.QUICK_ADD
              ? `Sol Ring (${environment.getOnMac() ? 'Cmd' : 'Ctrl'} + ')`
              : `is:shockland (${environment.getOnMac() ? 'Cmd' : 'Ctrl'} + .)`
          }
          className={styles.inputWrapper}
          noIcon
          triggerClassName={styles.input}
        />
      )}

      {addType === ADD_METHODS.DETAILED_ADD && (
        <>
          <PhatButton className={styles.detailedAddTrigger} onClick={() => setDetailedAddModal(true)}>
            Detailed Add ({environment.getOnMac() ? 'Cmd' : 'Ctrl'} + ;)
          </PhatButton>
          <DetailedAddModal
            open={detailedAddModal}
            onClose={() => setDetailedAddModal(false)}
            onAddCard={card =>
              onCardSelected(card, quickAddOptions.matchExistingBy, quickAddOptions.incrementOnDuplicate)
            }
          />
        </>
      )}

      <ArchidektDropdown
        menuClassName={styles.addTypeMenu}
        options={[
          {
            label: createKeybindLabel(
              ADD_METHOD_NAMES[1],
              'fast forward',
              `${environment.getOnMac() ? 'Cmd' : 'Ctrl'} + '`,
            ),
            onClick: () => handleUpdateOptions({ addType: ADD_METHODS.QUICK_ADD }),
          },
          {
            label: createKeybindLabel(ADD_METHOD_NAMES[3], 'code', `${environment.getOnMac() ? 'Cmd' : 'Ctrl'} + .`),
            onClick: () => handleUpdateOptions({ addType: ADD_METHODS.SYNTAX_QUICK_ADD }),
          },
          { type: 'spacer' },
          {
            type: 'extras',
            id: 'add-to-category-option',
            label: 'Add card to category...',
            icon: 'pencil',
            className: styles.addTypeMenuSubmenu,
            options: [
              {
                label: 'Automatic category',
                onClick: () => handleSelecteAddToCategory(null),
                icon: !addToCategory ? ('circle check' as IconTypes) : ('circle outline' as IconTypes),
              },
              { type: 'spacer' },
              ...Object.values(categories)
                .sort((a, b) => (a.name < b.name ? -1 : 1))
                .filter(category => !category.includedInDeck || category.name === 'Sideboard' || category.isPremier)
                .map(category => ({
                  label: category.name,
                  icon:
                    addToCategory === category.name ? ('circle check' as IconTypes) : ('circle outline' as IconTypes),
                  onClick: () => handleSelecteAddToCategory(category.name),
                })),
              { type: 'spacer' },
              ...Object.values(categories)
                .sort((a, b) => (a.name < b.name ? -1 : 1))
                .filter(category => category.includedInDeck && category.name !== 'Sideboard' && !category.isPremier)
                .map(category => ({
                  label: category.name,
                  icon:
                    addToCategory === category.name ? ('circle check' as IconTypes) : ('circle outline' as IconTypes),
                  onClick: () => handleSelecteAddToCategory(category.name),
                })),
            ],
          },
          {
            type: 'extras',
            id: 'card-match-type',
            label: 'Match existing cards by',
            icon: 'arrow right',
            options: [
              {
                icon: quickAddOptions.matchExistingBy === 'name' ? 'check circle' : 'circle outline',
                label: 'Card name only',
                onClick: () => handleUpdateOptions({ matchExistingBy: 'name' }),
              },
              {
                icon: quickAddOptions.matchExistingBy === 'uid' ? 'check circle' : 'circle outline',
                label: 'Exact printing',
                onClick: () => handleUpdateOptions({ matchExistingBy: 'uid' }),
              },
              { type: 'spacer' },
              {
                icon: quickAddOptions.matchExistingBy === 'never' ? 'check circle' : 'circle outline',
                label: 'Never match',
                onClick: () => handleUpdateOptions({ matchExistingBy: 'never' }),
              },
            ],
          },
          { type: 'spacer' },
          {
            type: 'checkbox',
            label: "Don't clear results on select",
            onChange: () => handleUpdateOptions({ dontClearOnSelect: !dontClearOnSelect }),
            checked: dontClearOnSelect,
            noCloseOnClick: true,
          },
          {
            type: 'checkbox',
            label: '+1 quantity on matching cards',
            onChange: () => handleUpdateOptions({ incrementOnDuplicate: !incrementOnDuplicate }),
            checked: incrementOnDuplicate,
            noCloseOnClick: true,
          },
          {
            type: 'custom',
            onAccessibleClick: () => handleUpdateOptions({ smartQuickAdd: !smartQuickAdd }),
            customChild: (
              <span className={styles.smartQuickAddOption}>
                <SimpleButton
                  icon={smartQuickAdd ? 'check square' : 'square outline'}
                  onClick={() => handleUpdateOptions({ smartQuickAdd: !smartQuickAdd })}>
                  Smart filtering
                </SimpleButton>

                <SimpleButton icon="question circle outline" onClick={() => setViewSmartDetailsInfo(true)} />

                <Modal closeButton open={viewSmartDetailsInfo} onClose={() => setViewSmartDetailsInfo(false)}>
                  <h3>Smart Filters Info</h3>

                  <ul>
                    <li>Limit to color identity (in applicible formats)</li>
                    <li>Limit results to the deck's game type (paper, mtgo, arena)</li>
                    <li>Limit results to only cards legal in this deck's format</li>
                  </ul>
                </Modal>
              </span>
            ),
          },
          { type: 'spacer' },
          {
            label: createKeybindLabel(
              ADD_METHOD_NAMES[2],
              'write square',
              `${environment.getOnMac() ? 'Cmd' : 'Ctrl'} + ;`,
            ),
            onClick: () => handleUpdateOptions({ addType: ADD_METHODS.DETAILED_ADD }),
          },
        ]}>
        <PhatButton className={styles.addTypeTrigger}>
          <Icon name="cog" />
        </PhatButton>
      </ArchidektDropdown>
    </div>
  )
}

export default QuickAddCard
